'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconHome, IconSearch } from '@repo/svgs';
import { useBedroomFilter } from './filters/hooks/useBedroomFilter';
import { usePriceFilter } from './filters/hooks/usePriceFilter';
import { useStore } from '@/providers/StoreProvider';

export interface ListingsMiniSearchBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  locationLabel?: string;
  priceLabel?: string;
  bedsLabel?: string;
  onLocationClick?: () => void;
  onPriceClick?: () => void;
  onBedsClick?: () => void;
  onSearchClick?: () => void;
  onLeftIconClick?: () => void;
}

export default function ListingsMiniSearchBar({
  locationLabel = 'All of NYC',
  priceLabel = 'Any price',
  bedsLabel = 'Any beds',
  onLocationClick,
  onPriceClick,
  onBedsClick,
  onSearchClick,
  onLeftIconClick,
  className,
  id,
  style,
  ...rest
}: ListingsMiniSearchBarProps) {
  const { formatPriceDisplay } = usePriceFilter();
  const { formatBedroomsDisplay } = useBedroomFilter();
  const selectionLabel = useStore(s => s.listingsFilters.getLocationSelectionLabel());

  return (
    <div
      className={cn(
        // Layout
        'flex items-center justify-between gap-2 w-auto border border-secondary',
        // Visual
        'rounded-full bg-primary shadow p-2',
        className,
      )}
      id={id}
      style={style}
      {...rest}
    >
      {/* Left icon button */}
      <button
        type="button"
        aria-label="Type: Rent apartments"
        className={cn(
          // Layout
          'inline-flex items-center justify-center h-9 w-9 p-2',
          // Shape/visual
          'rounded-full',
          // Visuals
          'bg-active icon-accent',
          // Interaction
          'cursor-pointer',
        )}
        tabIndex={-1}
        onClick={onLeftIconClick}
      >
        <IconHome width={16} height={16} />
      </button>

      {/* Input segments */}
      <button
        type="button"
        aria-label="Change location"
        className={cn(
          // Shape & spacing
          'rounded-full px-2 py-2',
          // Visuals
          'bg-primary text-secondary text-body-2',
          // Interactivity
          'cursor-pointer',
        )}
        onClick={onLocationClick}
      >
        {selectionLabel || locationLabel}
      </button>

      {/* Divider */}
      <div
        className={cn(
          // Size and color
          'h-6 border-l border-secondary',
        )}
      />

      <button
        type="button"
        aria-label="Change price"
        className={cn(
          // Shape & spacing
          'rounded-full px-2 py-2',
          // Visuals
          'bg-primary text-secondary text-body-2',
          // Interactivity
          'cursor-pointer',
        )}
        onClick={onPriceClick}
      >
        {formatPriceDisplay() || 'Any price'}
      </button>

      {/* Divider */}
      <div
        className={cn(
          // Size and color
          'h-6 border-l border-secondary',
        )}
      />

      <button
        type="button"
        aria-label="Change beds"
        className={cn(
          // Shape & spacing
          'rounded-full px-2 py-2',
          // Visuals
          'bg-primary text-secondary text-body-2',
          // Interactivity
          'cursor-pointer',
        )}
        onClick={onBedsClick}
      >
        {formatBedroomsDisplay() || 'Any beds'}
      </button>

      {/* Right icon button (submit) */}
      <button
        type="button"
        aria-label="Submit search"
        className={cn(
          // Layout
          'inline-flex items-center justify-center h-9 w-9 p-2',
          // Shape/visuals
          'rounded-full',
          // Visuals
          'bg-accent hover:bg-accent-hover icon-inverted',
        )}
        onClick={onSearchClick}
      >
        <IconSearch className="icon-inverted" width={16} height={16} />
      </button>
    </div>
  );
}
